import api from '@/factories/api';
import router from '@/router';

export default {
  namespaced: true,

  state: {
    loading: false,
    loginInfo: null,
    loginInfoLoaded: false,
    signUpInfo: null,
    CSRFToken: null,
  },

  mutations: {
    setLoading: (state, loading) => {
      state.loading = loading;
    },
    setLoginInfoLoaded: (state, loading) => {
      state.loginInfoLoaded = loading;
    },
    setLoginInfo: (state, info) => {
      state.loginInfo = info;
    },
    setCSRFToken: (state, token) => {
      state.CSRFToken = token;
    },
    setSignUpInfo: (state, info) => {
      state.signUpInfo = info;
    },
  },

  actions: {
    async logout({ commit }) {
      await api().post('users/logout');
      commit('users/setCurrentUser', null, { root: true });
      commit('users/setCurrentUserLoaded', false, { root: true });
    },

    async login({ commit, dispatch }, data) {
      commit('setLoading', true);
      try {
        return await dispatch('processAuthRequest', {
          data,
          method: 'post',
          path: 'users/login',
        });
      } catch (err) {
        throw err;
      } finally {
        commit('setLoading', false);
      }
    },

    async verifyLoginCode({ commit, dispatch }, data) {
      commit('setLoading', true);
      try {
        return await dispatch('processAuthRequest', {
          data,
          method: 'post',
          path: 'users/login_code',
        });
      } finally {
        commit('setLoading', false);
      }
    },

    async resendLoginCode({ dispatch }) {
      return dispatch('processAuthRequest', {
        data: {},
        method: 'post',
        path: 'users/login_code_resend',
      });
    },

    async signup({ commit, dispatch }, data) {
      try {
        return await dispatch('processAuthRequest', {
          data,
          method: 'post',
          path: 'users/signup',
        });
      } catch (err) {
        throw err;
      }
    },

    async validateSignupDomain({ dispatch }, data) {
      return dispatch('processAuthRequest', {
        data,
        method: 'post',
        path: 'users/check_domain',
      });
    },

    async resetPassword({ dispatch }, data) {
      try {
        return await dispatch('processAuthRequest', {
          data,
          method: 'post',
          path: 'users/reset_password',
        });
      } catch (err) {
        throw err;
      }
    },

    async prepareResetPassword({ dispatch }, params) {
      try {
        return await dispatch('processAuthRequest', {
          data: { params },
          method: 'get',
          path: 'users/reset_password_confirm',
        });
      } catch (err) {
        throw err;
      }
    },

    async setNewPassword({ commit, dispatch }, data) {
      commit('setLoading', true);
      try {
        return await dispatch('processAuthRequest', {
          data,
          method: 'post',
          path: 'users/reset_password_confirm',
        });
      } catch (err) {
        throw err;
      } finally {
        commit('setLoading', false);
      }
    },

    async getLoginInfo({ commit, state, dispatch }, code) {
      if (state.loginInfo || state.loginInfoLoaded) return;

      commit('setLoading', true);
      try {
        let path = 'users/login';
        if (code) {
          path += `?code=${code}`;
        }

        const res = await dispatch('processAuthRequest', {
          method: 'get',
          path,
        });

        if (res?.action === 'validate_okta') {
          router.push('/');
        }

        if (Array.isArray(res) && res.length === 0) {
          router.push('/');
        }

        commit('setLoginInfo', res);
      } finally {
        commit('setLoading', false);
        commit('setLoginInfoLoaded', true);
      }
    },

    async getSignUpInfo({ commit, state, dispatch }, plan = '') {
      if (state.signUpInfo) return;

      commit('setLoading', true);

      try {
        const res = await dispatch('processAuthRequest', {
          method: 'get',
          path: `users/signup?plan=${plan}`,
        });
        commit('setSignUpInfo', res);
      } finally {
        commit('setLoading', false);
      }
    },

    // extract or add CSRF-token
    async processAuthRequest({ commit, state, dispatch }, params) {
      const { data, method, path = '', csrfRetried } = params;
      let config = null;

      if (method === 'post' || path.includes('reset_password_confirm')) {
        config = {
          returnMessage: true,
          CSRFToken: state.CSRFToken || null,
        };
      }

      let res;
      try {
        res = await api(config)[method](path, data);
      } catch (err) {
        // a request still in flight at logout can swap in a new session; fetch its token and retry the login once
        if (
          path === 'users/login' &&
          method === 'post' &&
          err?.status === 403 &&
          !csrfRetried
        ) {
          await dispatch('processAuthRequest', {
            method: 'get',
            path: 'users/login',
          });
          return dispatch('processAuthRequest', {
            ...params,
            csrfRetried: true,
          });
        }
        throw err;
      }
      const csrf_token = res?.csrf_token || res?.data?.csrf_token;
      if (csrf_token) {
        commit('setCSRFToken', csrf_token);
      }

      return res;
    },
  },
};
